import { useState } from 'react';
import { Button } from '../ui/button';
import { STAFF_TEXT_MAX_CHARS } from '../../lib/leads/textRules';
import { DEMO_TEXT_MESSAGE } from '../../lib/leads/contactState';
import type { LeadSplitApi } from './LeadSplitView';

// "TEXT <NAME>" (Leads centre v2, Part C): a person writes a text to the lead from the lead screen. It sits BELOW the thread, in its own box
// (or on its own when the lead has no conversation yet). ⚠️ Nothing is sent until the person presses Send; nothing here runs on load. The
// SERVER decides everything (opted out, do not contact, demo, 7am-9pm club time, the hourly limit, the 480 characters): this box only counts
// and shows the server's own sentence when a send is refused. It names no number, and it says plainly that sending pauses the AI.
//
// ⚠️ A box that cannot be used right now is DISABLED WITH ITS REASON beside it (no permission, outside hours, opted out, do not contact), never
// hidden. ⚠️ On a DEMO lead the box works and Send answers "This is a demo lead, so nothing was sent." without calling the server at all.
// ⚠️ The "Sent" confirmation fades by CSS alone (no timer, no effect): it is gone from the page a few seconds later.

export interface TextLeadComposerProps {
  leadId: string;
  firstName: string;
  api: Pick<LeadSplitApi, 'sendText'>;
  /** The AI is already paused for this lead (so the pause note is not repeated). */
  paused: boolean;
  onSent?: () => void;
  /** Why the box cannot be used right now (the screen's own short sentence), or null. */
  disabledReason?: string | null;
  /** A demo lead: Send answers on the screen and never reaches the server. */
  demo?: boolean;
}

export function TextLeadComposer({ leadId, firstName, api, paused, onSent, disabledReason = null, demo = false }: TextLeadComposerProps) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [demoNote, setDemoNote] = useState(false);
  const length = text.trim().length;
  const tooLong = length > STAFF_TEXT_MAX_CHARS;
  const blocked = disabledReason !== null;

  const send = async () => {
    if (busy || blocked || length === 0 || tooLong) return;
    if (demo) {
      setDemoNote(true);
      return;
    }
    setBusy(true);
    setError(null);
    setSent(false);
    try {
      await api.sendText(leadId, text);
      setText('');
      setSent(true);
      onSent?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The text could not be sent.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="kc-composer space-y-2 rounded-[14px] border border-border bg-background p-3" data-testid="text-composer">
      <label className="kc-label block text-xs text-muted-foreground" htmlFor={`text-lead-${leadId}`}>Text {firstName} from the club&apos;s number</label>
      <textarea
        id={`text-lead-${leadId}`}
        className="kc-body min-h-[72px] w-full rounded border border-border bg-background p-2 text-sm text-foreground disabled:opacity-60"
        value={text}
        disabled={blocked}
        onChange={(e) => {
          setText(e.target.value);
          setSent(false);
          setDemoNote(false);
        }}
        placeholder={`Write a message to ${firstName}`}
        data-testid="text-input"
      />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className={`kc-body text-xs ${tooLong ? 'text-destructive' : 'text-muted-foreground'}`} data-testid="text-count">
          {length} / {STAFF_TEXT_MAX_CHARS}
        </p>
        <Button type="button" size="sm" className="kc-save" disabled={busy || blocked || length === 0 || tooLong} onClick={() => void send()} data-testid="text-send">
          {busy ? 'Sending…' : 'Send'}
        </Button>
      </div>
      {blocked && <p className="kc-body text-sm text-foreground" data-testid="text-disabled-reason">{disabledReason}</p>}
      {!paused && !blocked && <p className="kc-body text-xs text-muted-foreground" data-testid="text-pause-note">Sending a text pauses the AI for {firstName} until you hand it back.</p>}
      {sent && <p role="status" className="kc-sent-fade kc-body text-sm text-foreground" data-testid="text-sent">Sent.</p>}
      {demoNote && <p role="status" className="kc-body text-sm text-foreground" data-testid="text-demo-note">{DEMO_TEXT_MESSAGE}</p>}
      {error && <p role="alert" className="kc-body text-sm text-destructive" data-testid="text-error">{error}</p>}
    </div>
  );
}
